/**
 * SpatchVenire characteristic-based scorer (v1, reference/flagged).
 *
 * Implements the Go/No-Go model (docs/03_decision_model): questionnaire fields
 * -> 10 characteristic scores -> victim/defendant modifiers -> charge-cluster
 * weights -> evidence gate -> net prosecution/defense orientation -> asymmetric
 * GO/NO-GO/PROBE bands. Pure and sync (data from the generated `spatchvenire-
 * model`), so it runs alongside the legacy `scoreJuror`.
 *
 * Coverage: signal matchers cover the fields the app captures (occupation,
 * education, LE connection, victim/accused experience, prior jury, parent/age).
 * Case-attitude/NFC signals route to PROBEs. Victim/defendant modifiers (Step 4)
 * come from the case's victim/defendant records — see aggregateParties(). A
 * case may have several victims (a modifier activates if it applies to ANY
 * one of them — jurors will hear about all of them regardless) but only one
 * defendant.
 */

import type { Juror, ChargeSlug, Side, EvidenceStrength, PretrialPublicity, VictimRecord, DefendantRecord, VdAnswer } from '@/types';
import { jurorAge } from '@/core/scoring';
import { SV_SIGNALS, SV_CHARACTERISTICS, SV_CHARGE_WEIGHTS } from '@/data/spatchvenire-model';

/** victim_defendant_modifiers.csv row 13: high pretrial publicity -> per-juror exposure recall probe. */
const HIGH_PUBLICITY_PROBE =
  'Ask this juror to describe SPECIFICALLY what they recall from pretrial publicity about this case — detailed, specific recall (vs. vague awareness) supports a cause challenge (Steblay 1999/2006: instructions do not cure exposure).';

/**
 * venue-location-effects: "Community sentiment about local crime and victim-
 * community (in-group) bias" -> per-juror residence/connection screen. Case-
 * level fact (not a per-juror county weight — see the note on CaseProfile).
 */
const LOCAL_COMMUNITY_IMPACT_PROBE =
  "Screen this juror's residential proximity to the crime and any personal or second-degree connection to the victim, the neighborhood, or affected local institutions (MA's compact counties often create dense degrees-of-separation ties — shared schools, employers, towns). Generalized community fear/anger here supports a cause challenge, not just a peremptory (Tsarnaev, 595 U.S. 302 (2022); Steblay et al. 1999).";

/** Case-side inputs scoreJurorSV needs: the evidence gate + case posture + the recorded parties. */
export interface ScoringProfile {
  evidence: EvidenceStrength;
  pretrialPublicity: PretrialPublicity;
  localCommunityImpact: boolean;
  /** A case may have several victims. */
  victims: VictimRecord[];
  /** A case has exactly one defendant (or none yet recorded). */
  defendant: DefendantRecord | null;
}

/** Aggregated, scoring-relevant facts derived from the recorded parties. */
export interface PartyAggregate {
  victimCount: number;
  anyVictimChild: boolean;
  anyVictimLE: boolean;
  anyVictimInstitutional: boolean;
  anyConductContested: boolean;
  /** victim_defendant_modifiers.csv row 3: intimate/family relationship -> raise system-trust priors (Helm 2022). */
  anyIntimateOrFamilyRelationship: boolean;
  hasDefendant: boolean;
  defendantIsOfficer: boolean;
  defendantIsCorporate: boolean;
  defendantIsJuvenile: boolean;
}

/** Reduce the (possibly several) victims and the single defendant to scoring flags. */
export function aggregateParties(victims: VictimRecord[], defendant: DefendantRecord | null): PartyAggregate {
  return {
    victimCount: victims.length,
    anyVictimChild: victims.some((v) => v.ageClass === 'child'),
    anyVictimLE: victims.some((v) => v.isLE),
    anyVictimInstitutional: victims.some((v) => v.type === 'institutional'),
    anyConductContested: victims.some((v) => v.conductContested),
    anyIntimateOrFamilyRelationship: victims.some((v) => v.relationshipToDefendant === 'intimate-family'),
    hasDefendant: !!defendant,
    defendantIsOfficer: defendant?.status === 'officer',
    defendantIsCorporate: defendant?.status === 'corporate',
    defendantIsJuvenile: defendant?.status === 'juvenile'
  };
}

/**
 * App charge slug -> SpatchVenire charge cluster id (Part II matrix).
 * Cluster 2 ("Capital") is intentionally never routed here: Massachusetts
 * state courts have had no death penalty since Commonwealth v. Colon-Cruz,
 * 372 Mass. 405 (1980), so MA murder charges (cluster 1) are correctly never
 * capital — this is not a gap to fill for this app's MA state-court scope.
 */
const CHARGE_TO_CLUSTER: Record<ChargeSlug, number> = {
  murder: 1, assault: 1, sexual: 3, oui: 6, drug: 7, fraud: 10
};

const WEIGHT_NUM: Record<string, number> = { strong: 3, moderate: 2, weak: 1 };

/**
 * Voir-dire real-time adjustment (Step 6/7 of the Go/No-Go model): a recorded
 * answer overrides the model's estimate. Each rated answer contributes
 * +/-VD_FAVORABILITY_WEIGHT on the prosecution axis (favorable-prosecution =
 * +, favorable-defense = -, neutral/unrated = 0), scaled by the juror's
 * demeanor while giving THAT answer. Demeanor is deliberately a MULTIPLIER,
 * not an independent direction — demeanor alone isn't inherently pro- or
 * anti- either side; a confident answer is more reliable evidence of the
 * juror's actual leaning, an evasive one less so. `cause` is independent of
 * the numeric score (see hasCauseFlag) — it drives the cause-challenge
 * banner and the compliance log, not the orientation number.
 */
const VD_FAVORABILITY_WEIGHT = 5;
const VD_DEMEANOR_MULTIPLIER: Record<VdAnswer['demeanor'], number> = {
  unrated: 1, positive: 1.4, neutral: 1, negative: 0.6
};

/** Sum the voir-dire adjustment across all recorded answers (prosecution-axis). */
function voirDireAdjustment(answers: VdAnswer[] | undefined): number {
  if (!answers?.length) return 0;
  let adj = 0;
  for (const a of answers) {
    const base =
      a.favorability === 'favorable-prosecution' ? VD_FAVORABILITY_WEIGHT
      : a.favorability === 'favorable-defense' ? -VD_FAVORABILITY_WEIGHT
      : 0;
    adj += base * VD_DEMEANOR_MULTIPLIER[a.demeanor];
  }
  return adj;
}

/** Default scoring profile — used when counsel hasn't recorded case facts yet. */
export const DEFAULT_SCORING_PROFILE: ScoringProfile = {
  evidence: 'close',
  pretrialPublicity: 'low',
  localCommunityImpact: false,
  victims: [],
  defendant: null
};

interface Facts {
  occ: string; biz: string; edu: string; emp: string; lawEmp: string;
  leConnected: boolean; victim: boolean; accused: boolean; jury: string;
  parent: boolean; age: number;
}

function facts(j: Juror): Facts {
  const nonEmpty = (v: string | undefined): boolean => !!v && !/^\s*(no|none)\b/i.test(v);
  const le = (j.le || '').toLowerCase();
  return {
    occ: (j.occ || '').toLowerCase(),
    biz: (j.bizType || '').toLowerCase(),
    edu: (j.edu || '').toLowerCase(),
    emp: (j.empStatus || '').toLowerCase(),
    lawEmp: (j.lawEmp || '').toLowerCase(),
    leConnected: !!le && !/^\s*none/.test(le),
    victim: nonEmpty(j.victim),
    accused: nonEmpty(j.accused),
    jury: (j.jury || '').toLowerCase(),
    parent: /yes|parent|guardian|child/i.test(j.parent || ''),
    age: jurorAge(j)
  };
}

/** Per-signal predicates keyed by signal id (missing = not matchable / skipped). */
const MATCHERS: Record<number, (f: Facts) => boolean> = {
  1: (f) => f.leConnected || /police|correction|security|military|officer|sheriff|deputy|law enforcement/.test(f.occ),
  3: (f) => /account|audit|clerk|administrat|bookkeep/.test(f.occ),
  4: (f) => f.age >= 65,
  5: (f) => /artist|design|music|writer|paint|academ|professor|creative|actor|journal/.test(f.occ),
  6: (f) => f.accused,
  8: (f) => f.victim,
  9: (f) => f.accused,
  10: (f) => /guilty|convict/.test(f.jury),
  11: (f) => /hung|acquit|not guilty/.test(f.jury),
  13: (f) => /self.?employ|owner|contractor|freelance/.test(f.occ) || /self/.test(f.emp),
  14: (f) => /electr|plumb|carpenter|weld|mechanic|mason|hvac|\btrade/.test(f.occ),
  16: (f) => /physician|doctor|scientist|engineer|software|develop|statistic|lawyer|attorney|research|analyst/.test(f.occ),
  17: (f) => /graduate|master|phd|doctora|professional|jd|md|mba/.test(f.edu),
  18: (f) => /cashier|assembler|line worker|warehouse|retail|stock/.test(f.occ),
  19: (f) => /nurse|teacher|social work|counsel|therap|aide|caregiver|educat/.test(f.occ),
  21: (f) => f.accused,
  22: (f) => f.victim,
  29: (f) => f.victim,
  31: (f) => f.accused,
  33: (f) => /prosecut|court|clerk|paralegal|legal/.test(f.lawEmp),
  34: (f) => /jury|served|verdict|guilty|hung|acquit|forepers/.test(f.jury),
  35: (f) => /manage|sales|finance|executive|director|litigat/.test(f.occ),
  36: (f) => /nurse|social work|counsel|teacher|therap|aide/.test(f.occ),
  37: (f) => /sales|teach|manage|minist|pastor|clergy/.test(f.occ),
  38: (f) => /driver|\blab\b|records|archiv|custod/.test(f.occ),
  39: (f) => /forepers|foreman/.test(f.jury)
};

const CASE_ROUTERS: { id: number; test: (f: Facts, charge: ChargeSlug) => boolean }[] = [
  { id: 24, test: (f, c) => c === 'oui' && /bartend|hospitality|server|restaurant|\bbar\b/.test(f.occ) },
  { id: 27, test: (f, c) => c === 'fraud' && /adjuster|claims|insurance/.test(f.occ) },
  { id: 28, test: (f) => f.leConnected }
];

/** Base prosecution-axis sign for a characteristic + signal direction (pre-modifier). */
function charSign(charId: number, dir: string): number {
  switch (charId) {
    case 1: case 2: case 8: return dir === 'raises' || dir === 'carries' ? 1 : dir === 'lowers' ? -1 : 0;
    case 4: return dir === 'internal' ? 1 : dir === 'external' ? -1 : 0;
    case 6: return dir === 'complainant' ? 1 : dir === 'defendant' ? -1 : 0;
    case 3: case 9: return dir === 'raises' ? 1 : dir === 'lowers' ? -1 : 0;
    case 10: return dir === 'raises' ? -1 : dir === 'lowers' ? 1 : 0;
    default: return 0; // 5 (NFC), 7 (case attitudes) -> probe only
  }
}

export interface SvMatched {
  signalId: number; characteristic: string; cjqField: string; condition: string;
  direction: string; weight: string; tier: string; probe: string;
  sourceKeys: string[]; contribution: number;
}

export interface SvAssessment {
  /** Final orientation INCLUDING the voir-dire adjustment (what the bands/sideScore use). */
  orientation: number;
  /** The model-only estimate BEFORE any recorded voir-dire answers — for transparency. */
  modelOrientation: number;
  /** orientation - modelOrientation, i.e. how much recorded answers moved the estimate. */
  voirDireAdjustment: number;
  sideScore: number;
  prosecutionBand: 'GO' | 'NO-GO' | 'PROBE';
  defenseBand: 'GO' | 'NO-GO' | 'PROBE';
  matched: SvMatched[];
  probes: string[];
  evidence: EvidenceStrength;
  /** True if any recorded answer is flagged as a cause-challenge candidate. */
  hasCauseFlag: boolean;
}

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

export function scoreJurorSV(
  j: Juror,
  ctx: { side: Side; activeCharges: ChargeSlug[] },
  profile: ScoringProfile = DEFAULT_SCORING_PROFILE
): SvAssessment {
  const f = facts(j);
  const charges = ctx.activeCharges.length ? ctx.activeCharges : (['murder'] as ChargeSlug[]);
  const matched: SvMatched[] = [];
  const probes = new Set<string>();

  // 1) Per-characteristic base scores from matched signals (charge-independent).
  const charBase = new Array(11).fill(0);
  let char1Raw = 0;
  for (const sig of SV_SIGNALS) {
    const m = MATCHERS[sig.id];
    if (!m || !m(f)) continue;
    const w = WEIGHT_NUM[sig.weight] ?? (sig.weight === 'derived' ? 1 : 0);
    const sign = charSign(sig.charId, sig.direction);
    if (sign === 0) { if (sig.probe) probes.add(sig.probe); continue; }
    const base = sign * w;
    charBase[sig.charId] += base;
    if (sig.charId === 1) char1Raw += base;
    if (sig.probe) probes.add(sig.probe);
    matched.push({
      signalId: sig.id, characteristic: sig.characteristic, cjqField: sig.cjqField,
      condition: sig.condition, direction: sig.direction, weight: sig.weight, tier: sig.tier,
      probe: sig.probe, sourceKeys: sig.sourceKeys, contribution: base
    });
  }
  charBase[2] += char1Raw * 0.5; // signal 7: char-1 carries to char-2 at half weight

  // 2) Victim/defendant modifiers (Step 4) -> per-characteristic factor/sign.
  // A case may have several victims — a modifier fires if it applies to ANY
  // one of them (a case with a child victim AND an adult victim still raises
  // parent-empathy signals, since jurors hear about both) — but only one
  // defendant, so the defendant flags are plain single-record checks.
  const agg = aggregateParties(profile.victims, profile.defendant);
  const factor = new Array(11).fill(1);
  const msign = new Array(11).fill(1);
  if (agg.anyConductContested) { factor[3] *= 2; factor[7] *= 2; }
  if (agg.anyVictimInstitutional || agg.defendantIsCorporate) msign[9] *= -1;
  if (agg.anyVictimLE) factor[1] *= 1.5;
  if (agg.victimCount === 0) factor[6] = 0; // victimless -> zero empathy
  if (agg.defendantIsOfficer) { msign[1] *= -1; msign[8] *= -1; }
  if (agg.defendantIsJuvenile) factor[2] *= 1.5;
  if (agg.anyVictimChild && f.parent) charBase[6] += 2; // parent-empathy
  if (agg.anyIntimateOrFamilyRelationship) factor[8] *= 1.5; // credibility-contest priors decide (Helm 2022)
  if (profile.pretrialPublicity === 'high') probes.add(HIGH_PUBLICITY_PROBE);
  if (profile.localCommunityImpact) probes.add(LOCAL_COMMUNITY_IMPACT_PROBE);

  // 3) Charge weights (Step 3) + sum across active charges.
  let orientationSum = 0;
  for (const charge of charges) {
    const cl = SV_CHARGE_WEIGHTS.find((c) => c.clusterId === (CHARGE_TO_CLUSTER[charge] ?? 1));
    if (!cl) continue;
    for (const r of CASE_ROUTERS) {
      if (r.test(f, charge)) { const s = SV_SIGNALS.find((x) => x.id === r.id); if (s?.probe) probes.add(s.probe); }
    }
    let o = 0;
    for (let c = 1; c <= 10; c++) o += charBase[c] * (cl.w[c - 1] ?? 1) * factor[c] * msign[c];
    orientationSum += o;
  }

  // 4) Evidence gate (Step 1) + normalize to a readable [-10, 10].
  const ev = profile.evidence;
  const gate = ev === 'strong' ? 0.5 : ev === 'weak' ? 1.15 : 1;
  const modelOrientation = clamp(Math.round(((orientationSum / charges.length) * gate) / 3), -10, 10);

  // 5) Voir-dire real-time adjustment (Steps 6/7): a recorded answer overrides
  // the model's estimate. Applied AFTER the model computation, clamped together.
  const vdAdjustment = voirDireAdjustment(j.vdAnswers);
  const orientation = clamp(Math.round(modelOrientation + vdAdjustment), -10, 10);
  const hasCauseFlag = !!j.vdAnswers?.some((a) => a.cause);

  const prosecutionBand: SvAssessment['prosecutionBand'] =
    orientation >= 4 ? 'GO' : orientation <= -2 ? 'NO-GO' : 'PROBE';
  const defenseBand: SvAssessment['defenseBand'] =
    orientation <= -3 ? 'GO' : orientation >= 4 ? 'NO-GO' : 'PROBE';

  const sideScore = ctx.side === 'prosecution' ? orientation : -orientation;
  return {
    orientation,
    modelOrientation,
    voirDireAdjustment: orientation - modelOrientation,
    sideScore,
    prosecutionBand,
    defenseBand,
    matched,
    probes: [...probes],
    evidence: ev,
    hasCauseFlag
  };
}

export const SV_CHARACTERISTIC_NAMES = SV_CHARACTERISTICS;
